import { StandardEvents } from '@shopify/events';

// The native form owns the only cart request. A bundle selects one unit of each
// chosen Y; the main quantity remains owned by the native quantity input.
class PebbleProductUpsell extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.section?.addEventListener('pebble:collect-extras', this.collect);
    this.section?.addEventListener(StandardEvents.productSelect, this.onProductSelect);
    this.section?.addEventListener('change', this.update);
    this.section?.addEventListener('input', this.update);
    this.section?.addEventListener('click', this.onClick);
    this.section?.addEventListener('quantity-selector:update', this.update);
    this.addEventListener('keydown', this.onKeydown);
    this.update();
  }
  disconnectedCallback() {
    this.removeEventListener('keydown', this.onKeydown);
    this.section?.removeEventListener('pebble:collect-extras', this.collect);
    this.section?.removeEventListener(StandardEvents.productSelect, this.onProductSelect);
    this.section?.removeEventListener('change', this.update);
    this.section?.removeEventListener('input', this.update);
    this.section?.removeEventListener('click', this.onClick);
    this.section?.removeEventListener('quantity-selector:update', this.update);
  }
  onClick = (event) => {
    if (this.contains(event.target)) {
      const change = event.target.closest('[data-change]');
      const choice = event.target.closest('[data-choose]');
      const panel = this.querySelector('[data-choices]');
      if (change && panel) {
        panel.hidden = !panel.hidden;
        change.setAttribute('aria-expanded', String(!panel.hidden));
        if (!panel.hidden) panel.querySelector('[data-choose][aria-pressed="true"]')?.focus();
      }
      if (choice) {
        const wasChecked = this.rows.some(row => row.querySelector('[data-toggle]').checked);
        for (const row of this.rows) {
          row.hidden = row.dataset.productId !== choice.dataset.choose;
          row.querySelector('[data-toggle]').checked = !row.hidden && wasChecked;
          row.querySelector('[data-change]').setAttribute('aria-expanded', 'false');
        }
        panel.hidden = true;
        this.rows.find(row => !row.hidden)?.querySelector('[data-change]')?.focus();
      }
    }
    queueMicrotask(() => this.update(event));
  };
  onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const panel = this.querySelector('[data-choices]');
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    const button = this.rows.find(row => !row.hidden)?.querySelector('[data-change]');
    button?.setAttribute('aria-expanded', 'false');
    button?.focus();
    event.stopPropagation();
  };
  get rows() { return [...this.querySelectorAll('[data-bundle-item]')]; }
  get quantity() {
    const input = this.section?.querySelector('product-form-component input[name="quantity"], quantity-selector-component input[ref="quantityInput"]');
    return Math.max(1, Number(input?.value) || 1);
  }
  money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100);
  }
  update = (event) => {
    const rows = this.rows;
    if (!rows.length) return;
    // The desktop sticky details column moves upward when its height grows.
    // Preserve the visible block position, not just window.scrollY: Horizon
    // actually scrolls .page-wrapper. Run only for the customer's switch input.
    const preservePosition = !this.restoringViewport && event?.target?.matches?.('[data-toggle]') && this.contains(event.target);
    const viewportTop = preservePosition ? this.getBoundingClientRect().top : 0;
    let scrollContainer;
    let previousAnchor;
    if (preservePosition) {
      this.restoringViewport = true;
      for (let parent = this.parentElement; parent; parent = parent.parentElement) {
        if (/(auto|scroll)/.test(getComputedStyle(parent).overflowY) && parent.scrollHeight > parent.clientHeight) {
          scrollContainer = parent;
          break;
        }
      }
      scrollContainer ||= document.scrollingElement;
      previousAnchor = scrollContainer.style.overflowAnchor;
      scrollContainer.style.overflowAnchor = 'none';
    }
    const ready = this.dataset.available === 'true' && this.dataset.eligible === 'true' && !this.pending;
    for (const row of rows) {
      this.updateRow(row, ready && !row.hidden);
      row.querySelector('[data-change]').hidden = rows.length < 2;
    }
    for (const button of this.querySelectorAll('[data-choose]')) {
      button.setAttribute('aria-pressed', String(rows.some(row => !row.hidden && row.dataset.productId === button.dataset.choose)));
    }
    let total = Number(this.dataset.mainPrice) * this.quantity;
    let original = total;
    let hasSelection = false;
    for (const row of rows) {
      if (!row.querySelector('[data-toggle]').checked) continue;
      const option = row.querySelector('select').selectedOptions[0];
      total += Number(option.dataset.offerCents);
      original += Number(option.dataset.cents);
      hasSelection = true;
    }
    this.classList.toggle('is-selected', hasSelection);
    this.querySelector('[data-main-total]').textContent = this.money(Number(this.dataset.mainPrice) * this.quantity);
    this.querySelector('[data-main-caption]').textContent = `Main product · Quantity ${this.quantity}`;
    this.querySelector('[data-total]').textContent = this.money(total);
    const compare = this.querySelector('[data-total-original]');
    compare.hidden = original <= total;
    compare.textContent = this.money(original);
    if (scrollContainer) {
      // Force layout before restoring the anchor so the browser cannot apply
      // another automatic scroll adjustment after the sticky height changes.
      const delta = this.getBoundingClientRect().top - viewportTop;
      if (Math.abs(delta) > 0.5) scrollContainer.scrollBy({ top: delta, behavior: 'instant' });
      this.getBoundingClientRect();
      requestAnimationFrame(() => {
        // Input and change fire in the same activation; let focus and sticky
        // layout settle once before the next paint, without fighting user scroll.
        const remaining = this.getBoundingClientRect().top - viewportTop;
        if (Math.abs(remaining) > 0.5) scrollContainer.scrollBy({ top: remaining, behavior: 'instant' });
        this.getBoundingClientRect();
        requestAnimationFrame(() => {
          // Horizon updates its sticky header in response to the compensated
          // scroll. Account for that final layout pass, then release immediately.
          const finalDelta = this.getBoundingClientRect().top - viewportTop;
          if (Math.abs(finalDelta) > 0.5) scrollContainer.scrollBy({ top: finalDelta, behavior: 'instant' });
          this.getBoundingClientRect();
          scrollContainer.style.overflowAnchor = previousAnchor;
          this.restoringViewport = false;
        });
      });
    }
  };
  updateRow(row, ready) {
    const option = row.querySelector('select')?.selectedOptions[0];
    const toggle = row.querySelector('[data-toggle]');
    const available = option?.dataset.available === 'true';
    toggle.disabled = !ready || !available;
    if (toggle.disabled && !this.pending) toggle.checked = false;
    row.classList.toggle('is-selected', toggle.checked && !toggle.disabled);
    if (!option) return;
    row.querySelector('[data-offer-price]').textContent = toggle.checked ? option.dataset.offerPrice : option.dataset.price;
    const original = row.querySelector('[data-original-price]');
    if (original) {
      original.textContent = option.dataset.price;
      original.hidden = !toggle.checked || Number(option.dataset.offerCents) >= Number(option.dataset.cents);
    }
    const img = row.querySelector('img');
    if (img && option.dataset.image && img.getAttribute('src') !== option.dataset.image) {
      img.removeAttribute('srcset');
      img.src = option.dataset.image;
    }
    row.querySelector('[data-state]').textContent = this.pending ? 'Updating options…' : !available ? 'Currently unavailable' : !ready ? 'Currently unavailable' : toggle.checked ? 'Added to your bundle' : 'Add to your bundle';
  }
  collect = (event) => {
    if (this.pending || event.target.closest('product-card')) return;
    if (event.detail.productId !== this.dataset.product || event.detail.variantId !== this.dataset.variant || event.detail.sellingPlan) return;
    // Re-check availability before attaching the chosen add-on.
    this.update();
    for (const row of this.rows) {
      const toggle = row.querySelector('[data-toggle]');
      if (row.hidden || !toggle.checked || toggle.disabled) continue;
      const variantId = row.querySelector('select').value;
      if (!event.detail.items.some(item => item.variantId === variantId)) event.detail.items.push({ variantId, quantity: 1 });
    }
  };
  onProductSelect = (event) => {
    if (event.target.closest('product-card')) return;
    const generation = this.generation = (this.generation || 0) + 1;
    this.pending = true;
    this.update();
    event.promise.then(({ detail }) => {
      if (!this.isConnected || generation !== this.generation) return;
      const replacement = detail?.html?.querySelector(`pebble-product-upsell[data-block-id="${this.dataset.blockId}"]`);
      this.dataset.available = replacement?.dataset.available || 'false';
      this.dataset.eligible = replacement?.dataset.eligible || 'false';
      this.dataset.variant = replacement?.dataset.variant || '';
      this.dataset.mainPrice = replacement?.dataset.mainPrice || '0';
      this.pending = false;
      this.update();
    }).catch(() => { if (generation === this.generation) { this.pending = false; this.dataset.available = 'false'; this.update(); } });
  };
}
if (!customElements.get('pebble-product-upsell')) customElements.define('pebble-product-upsell', PebbleProductUpsell);
